import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { safeStorage } from '../utils/safeStorage';
import { logger } from '../utils/logger';

/**
 * SavedViewsContext
 * ---------------------------------------------------------------------------
 * Lets users snapshot the current `(route + filters + sort)` as a named view
 * and re-apply it in one click. Stored locally per-user in localStorage; in
 * a future iteration this can sync to the backend for cross-device parity.
 *
 * Shape of a saved view:
 *   { id, name, route, filters: { ... }, createdAt }
 *
 * Each route can attach an `apply(filters)` hook when the user clicks an
 * entry — the route reads filters and rehydrates its own state.
 */

const STORAGE_KEY = 'polkdesq_saved_views';
const SavedViewsContext = createContext(null);

function readStored() {
  try {
    const raw = safeStorage.getString(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

export function SavedViewsProvider({ children }) {
  const [views, setViews] = useState(readStored);

  useEffect(() => {
    try { safeStorage.setString(STORAGE_KEY, JSON.stringify(views)); } catch (err) { logger.warn('Saved views not persisted (storage disabled), falling back to in-memory:', err?.message); }
  }, [views]);

  const addView = useCallback((name, route, filters) => {
    if (!name?.trim()) return null;
    const view = {
      id: `sv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim().slice(0, 60),
      route,
      filters: filters || {},
      createdAt: new Date().toISOString(),
    };
    setViews((prev) => [view, ...prev].slice(0, 24)); // soft cap
    return view;
  }, []);

  const removeView = useCallback((id) => {
    setViews((prev) => prev.filter((v) => v.id !== id));
  }, []);

  const renameView = useCallback((id, newName) => {
    if (!newName?.trim()) return;
    setViews((prev) => prev.map((v) => (v.id === id ? { ...v, name: newName.trim().slice(0, 60) } : v)));
  }, []);

  const value = useMemo(() => ({ views, addView, removeView, renameView }), [views, addView, removeView, renameView]);
  return <SavedViewsContext.Provider value={value}>{children}</SavedViewsContext.Provider>;
}

export function useSavedViews() {
  const ctx = useContext(SavedViewsContext);
  if (!ctx) return { views: [], addView: () => null, removeView: () => {}, renameView: () => {} };
  return ctx;
}
